import { z } from 'zod';
import { AiAccountTenantScopeSchema } from '@terra/shared-contracts-ai-accounts';
import {
  AiEmbeddingModelNameWireSchema,
  AiModelNameWireSchema,
  EMBEDDING_MODEL_NAMES,
} from '@terra/shared-schemas-orchestrator';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import {
  TENANT_BEDROCK_ROUTING_SCOPES,
  TENANT_REASONING_EFFORTS,
  TENANT_REASONING_SETTINGS,
} from './tenant-model-router.constants';

const NonemptyStringSchema = z.string().min(1);

export const TenantBedrockRoutingScopeSchema = z.enum(
  TENANT_BEDROCK_ROUTING_SCOPES,
);

export const TenantReasoningEffortSchema = z.enum(TENANT_REASONING_EFFORTS);

export const TenantReasoningSettingSchema = z.enum(TENANT_REASONING_SETTINGS);

export const TenantModelRouterDeploymentSchema = z.discriminatedUnion(
  'platform',
  [
    z
      .object({
        platform: z.literal('aws'),
        region: z.enum(['us', 'eu']),
      })
      .strict(),
    z
      .object({
        platform: z.literal('gcp'),
        region: z.literal('us'),
      })
      .strict(),
  ],
);

export const TerraInvocationRoutingSchema =
  AiAccountTenantScopeSchema.safeExtend({
    accountId: NonemptyStringSchema.optional(),
    model: AiModelNameWireSchema,
    reasoningEffort: TenantReasoningSettingSchema.optional(),
  }).strict();

export const TerraInvocationRoutingPersistedSchema =
  TerraInvocationRoutingSchema.safeExtend({
    id: NonemptyStringSchema,
  }).strict();

export const TenantModelRouterInvocationRowSchema =
  TerraInvocationRoutingPersistedSchema.safeExtend({
    invocation: NonemptyStringSchema,
  }).strict();

export const TerraEmbeddingRoutingSchema =
  AiAccountTenantScopeSchema.safeExtend({
    accountId: NonemptyStringSchema.optional(),
    model: AiEmbeddingModelNameWireSchema,
  }).strict();

export const TerraEmbeddingRoutingPersistedSchema =
  TerraEmbeddingRoutingSchema.safeExtend({
    id: NonemptyStringSchema,
  }).strict();

const LEGACY_INVOCATION_RULE_ID_PREFIX = 'legacy';

const toPersistedInvocationRule = ({
  invocation,
  rule,
}: {
  invocation?: string;
  rule: unknown;
}): unknown => {
  if (typeof rule !== 'object' || rule === null) {
    return rule;
  }

  const record = rule as Record<string, unknown>;

  if (typeof record.id === 'string' && record.id.length > 0) {
    return rule;
  }

  const invocationKey =
    typeof invocation === 'string' && invocation.length > 0
      ? invocation
      : typeof record.invocation === 'string' && record.invocation.length > 0
        ? record.invocation
        : 'unknown';

  return {
    ...record,
    id: `${LEGACY_INVOCATION_RULE_ID_PREFIX}:${invocationKey}`,
  };
};

const normalizeInvocationCacheEntries = (entries: unknown): unknown => {
  if (typeof entries !== 'object' || entries === null) {
    return entries;
  }

  return Object.fromEntries(
    Object.entries(entries as Record<string, unknown>).map(
      ([invocation, value]) => {
        if (Array.isArray(value)) {
          return [
            invocation,
            value.map((rule) =>
              toPersistedInvocationRule({ invocation, rule }),
            ),
          ];
        }

        if (typeof value === 'object' && value !== null) {
          return [
            invocation,
            [toPersistedInvocationRule({ invocation, rule: value })],
          ];
        }

        return [invocation, value];
      },
    ),
  );
};

const withInvocationCachePresentLegacyShape = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  const record = value as Record<string, unknown>;

  if (record.entries === undefined) {
    return value;
  }

  return {
    ...record,
    entries: normalizeInvocationCacheEntries(record.entries),
  };
};

const withTenantModelRouterResponseLegacyInvocations = (
  value: unknown,
): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  const record = value as Record<string, unknown>;

  if (!Array.isArray(record.invocations)) {
    return value;
  }

  return {
    ...record,
    invocations: record.invocations.map((row) =>
      toPersistedInvocationRule({
        invocation:
          typeof row === 'object' &&
          row !== null &&
          typeof (row as Record<string, unknown>).invocation === 'string'
            ? ((row as Record<string, unknown>).invocation as string)
            : undefined,
        rule: row,
      }),
    ),
  };
};

const withInvocationPersistedRulesLegacyShape = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((rule) => toPersistedInvocationRule({ rule }));
  }

  if (typeof value === 'object' && value !== null) {
    return [toPersistedInvocationRule({ rule: value })];
  }

  return value;
};

export const TenantModelRouterResponseSchema = z.preprocess(
  withTenantModelRouterResponseLegacyInvocations,
  z
    .object({
      embeddings: z.array(TerraEmbeddingRoutingPersistedSchema).default([]),
      enabled: z.boolean(),
      invocations: z.array(TenantModelRouterInvocationRowSchema),
    })
    .strict(),
);

export const TenantModelRouterMutationResponseSchema =
  TenantModelRouterResponseSchema;

const TenantModelRouterBulkInvocationNamesSchema = z
  .array(NonemptyStringSchema)
  .min(1);

export const TenantModelRouterBulkMutationSchema = z.discriminatedUnion(
  'action',
  [
    z
      .object({
        action: z.literal('apply'),
        invocations: TenantModelRouterBulkInvocationNamesSchema,
        routing: TerraInvocationRoutingSchema,
      })
      .strict(),
    z
      .object({
        action: z.literal('reset'),
        invocations: TenantModelRouterBulkInvocationNamesSchema,
      })
      .strict(),
  ],
);

export const TenantModelRouterDeleteResponseSchema = z
  .object({
    success: z.literal(true),
  })
  .strict();

export const TenantModelRouterCatalogBedrockProviderSchema = z
  .object({
    provider: z.literal('bedrock'),
    regions: z.array(NonemptyStringSchema),
    scopes: z.array(TenantBedrockRoutingScopeSchema),
  })
  .strict();

export const TenantModelRouterCatalogVertexProviderSchema = z
  .object({
    locations: z.array(NonemptyStringSchema),
    provider: z.literal('vertex'),
  })
  .strict();

export const TenantModelRouterCatalogProviderSchema = z.discriminatedUnion(
  'provider',
  [
    TenantModelRouterCatalogBedrockProviderSchema,
    TenantModelRouterCatalogVertexProviderSchema,
  ],
);

export const TenantModelRouterCatalogModelSchema = z
  .object({
    displayName: NonemptyStringSchema,
    modelId: NonemptyStringSchema,
    name: AiModelNameWireSchema,
    providers: z.array(TenantModelRouterCatalogProviderSchema),
    reasoningEfforts: z.array(TenantReasoningSettingSchema),
    vendor: NonemptyStringSchema,
  })
  .strict();

export const TenantModelRouterAccountModelSchema = z
  .object({
    displayName: NonemptyStringSchema,
    lab: NonemptyStringSchema,
    modelId: NonemptyStringSchema,
    name: AiModelNameWireSchema,
  })
  .strict();

export const TenantModelRouterEmbeddingModelSchema = z
  .object({
    dimensions: z.number().int().positive(),
    displayName: NonemptyStringSchema,
    name: AiEmbeddingModelNameWireSchema,
  })
  .strict();

export const TenantModelRouterCatalogInvocationSchema = z
  .object({
    alias: NonemptyStringSchema,
    category: z.enum([
      'codebase-intelligence',
      'security-testing',
      'analysis-and-validation',
      'automation',
    ]),
    defaultModel: AiModelNameWireSchema.optional(),
    description: NonemptyStringSchema,
    displayName: NonemptyStringSchema,
    name: NonemptyStringSchema,
    structuredOutput: z.boolean().default(false),
  })
  .strict();

type TenantModelRouterEmbeddingModel = z.infer<
  typeof TenantModelRouterEmbeddingModelSchema
>;

const LEGACY_EMBEDDING_MODEL_BY_PLATFORM = {
  aws: {
    dimensions: 1536,
    displayName: 'Amazon Titan Embed Text v1',
    name: EMBEDDING_MODEL_NAMES.TITAN_EMBED_TEXT_V1,
  },
  gcp: {
    dimensions: 768,
    displayName: 'Text Embedding 004',
    name: EMBEDDING_MODEL_NAMES.TEXT_EMBEDDING_004,
  },
} as const satisfies Record<'aws' | 'gcp', TenantModelRouterEmbeddingModel>;

const isEmbeddingCatalogModel = (
  value: unknown,
): value is TenantModelRouterEmbeddingModel =>
  TenantModelRouterEmbeddingModelSchema.safeParse(value).success;

const resolveLegacyEmbeddingModel = ({
  deployment,
}: {
  deployment: unknown;
}): TenantModelRouterEmbeddingModel | undefined => {
  if (typeof deployment !== 'object' || deployment === null) {
    return undefined;
  }

  const platform = (deployment as { platform?: unknown }).platform;

  if (platform === 'aws' || platform === 'gcp') {
    return LEGACY_EMBEDDING_MODEL_BY_PLATFORM[platform];
  }

  return undefined;
};

const withEmbeddingCatalogDefaults = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  const record = value as Record<string, unknown>;
  const accountEmbeddingModels = Array.isArray(record.accountEmbeddingModels)
    ? record.accountEmbeddingModels.filter(isEmbeddingCatalogModel)
    : [];
  const embeddingModels = Array.isArray(record.embeddingModels)
    ? record.embeddingModels.filter(isEmbeddingCatalogModel)
    : accountEmbeddingModels;
  const embeddingModel = isEmbeddingCatalogModel(record.embeddingModel)
    ? record.embeddingModel
    : (embeddingModels[0] ??
      accountEmbeddingModels[0] ??
      resolveLegacyEmbeddingModel({ deployment: record.deployment }));

  return {
    ...record,
    accountEmbeddingModels,
    embeddingModel,
    embeddingModels,
  };
};

const withInvocationAliases = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  const record = value as Record<string, unknown>;

  if (!Array.isArray(record.invocations)) {
    return value;
  }

  return {
    ...record,
    invocations: record.invocations.map((invocation: unknown) => {
      if (typeof invocation !== 'object' || invocation === null) {
        return invocation;
      }

      const invocationRecord = invocation as Record<string, unknown>;

      if (typeof invocationRecord.name !== 'string') {
        return invocation;
      }

      if (
        typeof invocationRecord.alias === 'string' &&
        invocationRecord.alias.length > 0
      ) {
        return invocation;
      }

      return { ...invocationRecord, alias: invocationRecord.name };
    }),
  };
};

export const TenantModelRouterAccountEmbeddingModelSchema =
  TenantModelRouterEmbeddingModelSchema;

export const TenantModelRouterCatalogObjectSchema = z
  .object({
    accountEmbeddingModels: z
      .array(TenantModelRouterAccountEmbeddingModelSchema)
      .default([]),
    accountModels: z.array(TenantModelRouterAccountModelSchema),
    deployment: TenantModelRouterDeploymentSchema,
    embeddingModel: TenantModelRouterEmbeddingModelSchema,
    embeddingModels: z.array(TenantModelRouterEmbeddingModelSchema),
    invocations: z.array(TenantModelRouterCatalogInvocationSchema),
    models: z.array(TenantModelRouterCatalogModelSchema),
  })
  .strict();

export const TenantModelRouterCatalogSchema = z.preprocess(
  (value) => withEmbeddingCatalogDefaults(withInvocationAliases(value)),
  TenantModelRouterCatalogObjectSchema,
);

export const TenantModelRouterCacheAbsentSchema = z
  .object({
    state: z.literal('absent'),
  })
  .strict();

export const TerraInvocationRoutingPersistedRulesSchema = z.preprocess(
  withInvocationPersistedRulesLegacyShape,
  z.array(TerraInvocationRoutingPersistedSchema),
);

export const TenantModelRouterInvocationsCachePresentSchema = z
  .object({
    entries: z.record(
      z.string(),
      z.array(TerraInvocationRoutingPersistedSchema),
    ),
    state: z.literal('present'),
  })
  .strict();

const withInvocationsCacheDocumentLegacyShape = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  const record = value as Record<string, unknown>;

  if (record.state !== 'present') {
    return value;
  }

  return withInvocationCachePresentLegacyShape(value);
};

export const TenantModelRouterInvocationsCacheDocumentSchema = z.preprocess(
  withInvocationsCacheDocumentLegacyShape,
  z.discriminatedUnion('state', [
    TenantModelRouterCacheAbsentSchema,
    TenantModelRouterInvocationsCachePresentSchema,
  ]),
);

export const TenantModelRouterEmbeddingsCachePresentSchema = z
  .object({
    rules: z.array(TerraEmbeddingRoutingPersistedSchema),
    state: z.literal('present'),
  })
  .strict();

export const TenantModelRouterEmbeddingsCacheDocumentSchema =
  z.discriminatedUnion('state', [
    TenantModelRouterCacheAbsentSchema,
    TenantModelRouterEmbeddingsCachePresentSchema,
  ]);

export const TerraInvocationRoutingRpcResponseSchema = z
  .object({
    embeddings: TenantModelRouterEmbeddingsCacheDocumentSchema.default({
      state: 'absent',
    }),
    invocations: TenantModelRouterInvocationsCacheDocumentSchema,
  })
  .strict();

export const TenantModelRouterInvocationParamsSchema = z
  .object({
    invocation: NonemptyStringSchema,
  })
  .strict();

export const TenantModelRouterInvocationRuleParamsSchema = z
  .object({
    id: MongoObjectIdSchema,
    invocation: NonemptyStringSchema,
  })
  .strict();

export const TenantModelRouterRuleIdParamsSchema = z
  .object({
    id: MongoObjectIdSchema,
  })
  .strict();
